import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

type StatusFilter = 'PENDING' | 'WAITLISTED' | 'CONVERTED' | 'REJECTED' | 'ALL';

const STATUS_TABS: StatusFilter[] = ['PENDING', 'WAITLISTED', 'CONVERTED', 'REJECTED', 'ALL'];

const statusColors: Record<string, string> = {
  PENDING: 'bg-amber-900/50 text-amber-300',
  WAITLISTED: 'bg-blue-900/50 text-blue-300',
  CONVERTED: 'bg-green-900/50 text-green-300',
  REJECTED: 'bg-red-900/50 text-red-300',
  UNVERIFIED: 'bg-slate-800 text-slate-400',
};

const platformLabels: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  both: 'Both',
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function BetaRequestsPage() {
  const { idToken } = useAuth();
  const api = createApiClient(idToken!);
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const queryStatus = statusFilter === 'ALL' ? undefined : statusFilter;

  const { data, isLoading, error } = useQuery({
    queryKey: ['beta-requests', statusFilter],
    queryFn: () => api.listBetaRequests({ status: queryStatus, limit: 50 }),
  });

  const requests = data?.requests || [];

  const clearMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setErrorMessage(null);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const showError = (msg: string) => {
    setErrorMessage(msg);
    setSuccessMessage(null);
    setTimeout(() => setErrorMessage(null), 6000);
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['beta-requests'] });
  };

  // Single approve (one-click: approve + convert)
  const handleApprove = useCallback(async (requestId: string) => {
    clearMessages();
    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      const result = await api.convertBetaRequest(requestId);
      invalidateQueries();
      if (result.emailSent === false && !result.userAlreadyExisted) {
        showSuccess('Account created, but welcome email failed to send.');
      } else {
        showSuccess('Beta request approved and account created.');
      }
    } catch (err) {
      showError(`Failed to approve: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  }, [api]);

  // Status update (waitlist, reject)
  const handleStatusUpdate = useCallback(async (requestId: string, status: string) => {
    clearMessages();
    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      await api.updateBetaRequest(requestId, { status });
      invalidateQueries();
      showSuccess(`Request ${status.toLowerCase()}.`);
    } catch (err) {
      showError(`Failed to update: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  }, [api]);

  // Bulk approve
  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    clearMessages();
    setBulkLoading(true);
    try {
      const result = await api.bulkApproveBetaRequests(Array.from(selectedIds));
      invalidateQueries();
      setSelectedIds(new Set());
      const { summary } = result;
      if (summary.failed > 0) {
        showSuccess(`Approved ${summary.success} of ${summary.total}. ${summary.failed} failed.`);
      } else {
        showSuccess(`All ${summary.success} requests approved and accounts created.`);
      }
    } catch (err) {
      showError(`Bulk approve failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setBulkLoading(false);
    }
  }, [api, selectedIds]);

  // Bulk reject
  const handleBulkReject = useCallback(async () => {
    if (selectedIds.size === 0) return;
    clearMessages();
    setBulkLoading(true);
    try {
      let successCount = 0;
      for (const id of selectedIds) {
        try {
          await api.updateBetaRequest(id, { status: 'REJECTED' });
          successCount++;
        } catch {
          // continue
        }
      }
      invalidateQueries();
      setSelectedIds(new Set());
      showSuccess(`Rejected ${successCount} request${successCount !== 1 ? 's' : ''}.`);
    } catch (err) {
      showError(`Bulk reject failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setBulkLoading(false);
    }
  }, [api, selectedIds]);

  // Bulk waitlist
  const handleBulkWaitlist = useCallback(async () => {
    if (selectedIds.size === 0) return;
    clearMessages();
    setBulkLoading(true);
    try {
      let successCount = 0;
      for (const id of selectedIds) {
        try {
          await api.updateBetaRequest(id, { status: 'WAITLISTED' });
          successCount++;
        } catch {
          // continue
        }
      }
      invalidateQueries();
      setSelectedIds(new Set());
      showSuccess(`Waitlisted ${successCount} request${successCount !== 1 ? 's' : ''}.`);
    } catch (err) {
      showError(`Bulk waitlist failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setBulkLoading(false);
    }
  }, [api, selectedIds]);

  // Delete
  const handleDelete = useCallback(async (requestId: string, name: string) => {
    if (!confirm(`Delete beta request from "${name}"? This cannot be undone.`)) return;
    clearMessages();
    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      await api.deleteBetaRequest(requestId);
      invalidateQueries();
      showSuccess('Beta request deleted.');
    } catch (err) {
      showError(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  }, [api]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const actionableIds = requests
      .filter((r: any) => r.status === 'PENDING' || r.status === 'WAITLISTED')
      .map((r: any) => r.requestId);
    if (selectedIds.size === actionableIds.length && actionableIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(actionableIds));
    }
  };

  const actionableRequests = requests.filter(
    (r: any) => r.status === 'PENDING' || r.status === 'WAITLISTED'
  );
  const allSelected = actionableRequests.length > 0 && selectedIds.size === actionableRequests.length;
  const showBulkBar = selectedIds.size > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-white">Beta Requests</h1>
        <div className="flex gap-2">
          {STATUS_TABS.map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setSelectedIds(new Set());
                clearMessages();
              }}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                statusFilter === status
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 px-4 py-3 bg-green-900/30 border border-green-800 rounded-lg text-green-300 text-sm">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-800 rounded-lg text-red-300 text-sm">
          {errorMessage}
        </div>
      )}

      {isLoading && <div className="text-slate-400">Loading requests...</div>}
      {error && <div className="text-red-400">Error: {(error as Error).message}</div>}

      {data && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs text-slate-500 uppercase">
                {(statusFilter === 'PENDING' || statusFilter === 'WAITLISTED' || statusFilter === 'ALL') && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="accent-orange-600"
                    />
                  </th>
                )}
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req: any) => {
                const isActionable = req.status === 'PENDING' || req.status === 'WAITLISTED';
                const isSelected = selectedIds.has(req.requestId);
                const loading = actionLoading[req.requestId];

                return (
                  <tr
                    key={req.requestId}
                    className={`border-b border-slate-800/50 hover:bg-slate-800/30 ${
                      isSelected ? 'bg-slate-800/20' : ''
                    }`}
                  >
                    {(statusFilter === 'PENDING' || statusFilter === 'WAITLISTED' || statusFilter === 'ALL') && (
                      <td className="px-4 py-3">
                        {isActionable && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(req.requestId)}
                            className="accent-orange-600"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-white">{req.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-300 font-mono">{req.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {platformLabels[req.platform] || req.platform}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded ${
                          statusColors[req.status] || 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {req.status}
                      </span>
                      {req.status === 'CONVERTED' && req.convertedAt && (
                        <span className="ml-2 text-xs text-slate-500">
                          {timeAgo(req.convertedAt)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500" title={new Date(req.createdAt).toLocaleString()}>
                      {timeAgo(req.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {req.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => handleApprove(req.requestId)}
                              disabled={loading}
                              className="px-2.5 py-1 text-xs font-medium bg-green-900/50 text-green-300 hover:bg-green-800/50 rounded transition-colors disabled:opacity-50"
                            >
                              {loading ? '...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(req.requestId, 'WAITLISTED')}
                              disabled={loading}
                              className="px-2.5 py-1 text-xs font-medium bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 rounded transition-colors disabled:opacity-50"
                            >
                              Waitlist
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(req.requestId, 'REJECTED')}
                              disabled={loading}
                              className="px-2.5 py-1 text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50 rounded transition-colors disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {req.status === 'WAITLISTED' && (
                          <>
                            <button
                              onClick={() => handleApprove(req.requestId)}
                              disabled={loading}
                              className="px-2.5 py-1 text-xs font-medium bg-green-900/50 text-green-300 hover:bg-green-800/50 rounded transition-colors disabled:opacity-50"
                            >
                              {loading ? '...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(req.requestId, 'REJECTED')}
                              disabled={loading}
                              className="px-2.5 py-1 text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50 rounded transition-colors disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {req.status === 'REJECTED' && (
                          <button
                            onClick={() => handleDelete(req.requestId, req.name)}
                            disabled={loading}
                            className="px-2.5 py-1 text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50 rounded transition-colors disabled:opacity-50"
                          >
                            {loading ? '...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {requests.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} beta requests found
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {data?.nextToken && (
        <div className="mt-4 text-center">
          <button className="px-4 py-2 text-sm bg-slate-800 text-slate-400 hover:bg-slate-700 rounded transition-colors">
            Load more
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {showBulkBar && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-slate-800 border border-slate-700 rounded-xl px-6 py-3 shadow-2xl z-50">
          <span className="text-sm text-white font-medium">{selectedIds.size} selected</span>
          <div className="w-px h-6 bg-slate-600" />
          <button
            onClick={handleBulkApprove}
            disabled={bulkLoading}
            className="px-3 py-1.5 text-xs font-medium bg-green-900/50 text-green-300 hover:bg-green-800/50 rounded transition-colors disabled:opacity-50"
          >
            {bulkLoading ? 'Processing...' : 'Approve All'}
          </button>
          <button
            onClick={handleBulkWaitlist}
            disabled={bulkLoading}
            className="px-3 py-1.5 text-xs font-medium bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 rounded transition-colors disabled:opacity-50"
          >
            Waitlist All
          </button>
          <button
            onClick={handleBulkReject}
            disabled={bulkLoading}
            className="px-3 py-1.5 text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50 rounded transition-colors disabled:opacity-50"
          >
            Reject All
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 text-xs text-slate-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
